import React from 'react';
import { TrendingUp, PieChart } from 'lucide-react';
import { mockBudgets, formatCurrency } from '@/utils/mockData';

export default function BudgetPage() {
  const totalAllocated = mockBudgets.reduce((sum, b) => sum + b.allocated, 0);
  const totalSpent = mockBudgets.reduce((sum, b) => sum + b.spent, 0);

  return (
    <div className="max-w-6xl mx-auto" data-testid="budget-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold font-heading text-slate-900">Budget & Analytics</h1>
        <p className="text-slate-600 mt-1">Track your spending and financial insights</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 text-brand-blue flex items-center justify-center">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Total Budget</p>
              <p className="text-2xl font-bold font-heading text-slate-900">{formatCurrency(totalAllocated)}</p>
            </div>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-brand-blue rounded-full" style={{width: `${(totalSpent/totalAllocated)*100}%`}}></div>
          </div>
          <p className="text-sm text-slate-600 mt-2">{formatCurrency(totalSpent)} spent • {formatCurrency(totalAllocated - totalSpent)} remaining</p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center">
              <PieChart className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Spending Rate</p>
              <p className="text-2xl font-bold font-heading text-slate-900">{((totalSpent/totalAllocated)*100).toFixed(0)}%</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <h3 className="text-lg font-bold font-heading text-slate-900 mb-6">Budget Breakdown</h3>
        <div className="space-y-6">
          {mockBudgets.map((budget, idx) => {
            const percent = (budget.spent / budget.allocated) * 100;
            const isOverspent = percent > 100;
            return (
              <div key={idx} data-testid={`budget-${budget.category.toLowerCase()}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: `${budget.color}20`, color: budget.color}}>
                      <TrendingUp className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{budget.category}</p>
                      <p className="text-sm text-slate-600">{formatCurrency(budget.spent)} of {formatCurrency(budget.allocated)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${isOverspent ? 'text-red-600' : 'text-slate-900'}`}>{percent.toFixed(0)}%</p>
                    <p className="text-sm text-slate-600">{formatCurrency(budget.allocated - budget.spent)} left</p>
                  </div>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{width: `${Math.min(percent, 100)}%`, backgroundColor: budget.color}}></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
